/**
 * Types du planning : la semaine à venir, et ce qui cloche dedans.
 *
 * Tout ce fichier est pur. Il ne lit rien, n'écrit rien, et c'est ce qui
 * permet de l'éprouver sans base — la grille, le repérage des conflits et
 * leur formulation sortent tous d'ici, donc l'écran et l'agent disent
 * exactement la même chose du même planning.
 */
import type { Employee } from "../team/types.js";
import { employeeDisplayName, startOfWeek, weekDates } from "../team/types.js";
import type { Assignment, DayPart, HalfDay, Mission } from "../field/types.js";
import type { WeekendDay } from "../field/types.js";
import {
  coversDay,
  dayPartOf,
  HALF_DAYS,
  halvesOf,
  isActiveOn,
  weekendDayOf,
} from "../field/types.js";
import type { ProjectStatus, ProjectSummary } from "../projects/types.js";

/**
 * Nature d'une absence prévue.
 *
 * Le vocabulaire de `TimeEntryKind`, moins ce qui ne se prévoit pas :
 * l'intempérie ne s'inscrit pas trois semaines à l'avance, et le travail et le
 * trajet ne sont pas des absences.
 */
export type AbsenceKind = "paid_leave" | "unpaid_leave" | "sick" | "training" | "other";

/**
 * Où en est une absence.
 *
 * `demandee` naît d'un ouvrier qui demande depuis son téléphone ; `accordee`
 * est l'état d'une absence posée par le bureau, et celui d'une demande
 * acceptée. `refusee` se conserve plutôt que de se supprimer : effacer un
 * refus ferait disparaître la réponse, et l'ouvrier ne saurait pas s'il a été
 * refusé ou oublié.
 */
export type AbsenceStatus = "demandee" | "accordee" | "refusee";

export interface Absence {
  id: string;
  employeeId: string;
  employeeName: string | null;
  kind: AbsenceKind;
  startsOn: string;
  /** Fermée : une absence sans fin est un départ, et un départ se dit ailleurs. */
  endsOn: string;
  notes: string | null;
  status: AbsenceStatus;
  /** Renseignés dès qu'une réponse a été donnée. */
  decidedAt: string | null;
  decisionNote: string | null;
}

/**
 * Cette absence vide-t-elle la case du planning ?
 *
 * **Seule une absence accordée compte comme une absence.** Une demande en
 * attente qui viderait déjà la case ferait croire à un manque d'effectif que
 * personne n'a décidé — et le ferait taire le jour où le patron refuse.
 *
 * La règle vit ici et les fonctions pures du planning l'appliquent
 * elles-mêmes : aucun écran n'a à s'en souvenir, et un écran de plus
 * n'introduira pas une seconde interprétation.
 */
export function blocksPlanning(absence: Pick<Absence, "status">): boolean {
  return absence.status === "accordee";
}

/** Ce qui attend une réponse du patron. */
export function isPendingRequest(absence: Pick<Absence, "status">): boolean {
  return absence.status === "demandee";
}

const ABSENCE_LABELS: Record<AbsenceKind, string> = {
  paid_leave: "Congé payé",
  unpaid_leave: "Congé sans solde",
  sick: "Arrêt maladie",
  training: "Formation",
  other: "Absent",
};

export function absenceKindLabel(kind: AbsenceKind): string {
  return ABSENCE_LABELS[kind];
}

/**
 * Un chantier tel qu'il apparaît dans une case du planning. Aucun montant.
 *
 * `assignmentId` y figure parce qu'une case montre une **affectation**, pas un
 * chantier : c'est elle qu'on retire d'un clic, et sans son identifiant le
 * bouton n'aurait rien à viser. C'est aussi ce qui distingue deux lignes
 * posées sur le même chantier.
 */
export interface PlannedProject {
  assignmentId: string;
  projectId: string;
  projectName: string;
  roleOnSite: string | null;
  /** La journée, le matin ou l'après-midi. */
  dayPart: DayPart;
}

/** Une case : un salarié, un jour. */
export interface PlanningCell {
  date: string;
  projects: PlannedProject[];
  /** L'absence qui couvre ce jour, s'il y en a une. */
  absence: Absence | null;
  /** Les demi-journées où il est sur un chantier, dans l'ordre de la journée. */
  busyHalves: HalfDay[];
  /**
   * Ni chantier ni absence, **un jour ouvré** — quelqu'un dont on n'a rien fait
   * ce jour-là. Jamais vrai un samedi ou un dimanche : un week-end vide est la
   * règle, pas un trou à combler. Un jour pris le matin seulement n'est pas
   * libre : sa moitié libre se lit dans `busyHalves`.
   */
  free: boolean;
  /** Samedi ou dimanche, sinon `null`. Un chantier ce jour-là est du travail de week-end, décidé. */
  weekend: WeekendDay | null;
}

/**
 * Les comptes de la semaine, **en jours et demi-jours** : un matin vaut 0,5.
 * Une semaine de quatre journées et un matin fait 4,5 jours affectés et
 * 0,5 jour libre.
 */
export interface PlanningRow {
  employee: Employee;
  days: PlanningCell[];
  /** Jours de la semaine où le salarié est sur un chantier. */
  assignedDays: number;
  absentDays: number;
  /** Jours ouvrés sans chantier ni absence, demi-journées libres comprises. */
  freeDays: number;
  /**
   * Samedis et dimanches travaillés cette semaine. Comptés à part parce que ce
   * sont des heures supplémentaires probables : le patron les a voulus
   * visibles, pas fondus dans les jours affectés.
   */
  weekendDays: number;
}

/**
 * Ce qui cloche dans la semaine.
 *
 * Trois natures seulement, et aucune n'est refusée par la base — voir la
 * migration 20260301002200. Un planning qui refuse ne se fait pas corriger, il
 * se fait contourner : on note la vraie affectation sur un carnet et l'outil
 * cesse de dire la vérité.
 */
export type PlanningConflictKind =
  | "assigned_while_absent"
  | "unstaffed_project"
  | "double_booking";

export interface PlanningConflict {
  kind: PlanningConflictKind;
  /** Bornes du problème, rognées à la semaine affichée. */
  from: string;
  to: string;
  employeeId: string | null;
  employeeName: string | null;
  projects: { id: string; name: string }[];
  /** Renseignée pour `assigned_while_absent` seulement. */
  absenceKind: AbsenceKind | null;
  /**
   * Pour `double_booking` : le moment où les chantiers se recouvrent — le
   * matin, l'après-midi, ou toute la journée. `null` pour les autres.
   */
  dayPart: DayPart | null;
}

export interface PlanningWeek {
  /** Lundi, AAAA-MM-JJ. */
  weekStart: string;
  /** Les sept dates, du lundi au dimanche. */
  dates: string[];
  rows: PlanningRow[];
  conflicts: PlanningConflict[];
}

/**
 * Deux périodes se recouvrent-elles ?
 *
 * `aEnd` nulle vaut « sans terme connu » : une affectation sur un chantier
 * long n'a pas de fin le jour où on la pose, et exiger une date en ferait
 * saisir une fausse. Une date fausse vaut moins que pas de date, mais elle
 * doit se comparer comme un infini, pas comme un vide.
 */
export function periodsOverlap(
  aStart: string,
  aEnd: string | null,
  bStart: string,
  bEnd: string,
): boolean {
  if (aStart > bEnd) return false;
  return aEnd == null || aEnd >= bStart;
}

/** L'intersection de deux périodes, la seconde étant fermée. Vide → `null`. */
function clampTo(
  aStart: string,
  aEnd: string | null,
  bStart: string,
  bEnd: string,
): { from: string; to: string } | null {
  if (!periodsOverlap(aStart, aEnd, bStart, bEnd)) return null;
  return {
    from: aStart > bStart ? aStart : bStart,
    to: aEnd == null || aEnd > bEnd ? bEnd : aEnd,
  };
}

/**
 * Les chantiers dont on attend qu'ils avancent cette semaine.
 *
 * `paused` n'en est pas : un chantier à l'arrêt n'a personne dessus **exprès**,
 * et le signaler chaque lundi apprendrait à ignorer la liste. `permit_pending`
 * non plus — on attend une mairie, pas une équipe.
 */
const ATTENDUS: ReadonlySet<ProjectStatus> = new Set<ProjectStatus>([
  "in_progress",
  "planned",
]);

/**
 * Ce chantier devrait-il avoir quelqu'un pendant cette semaine ?
 *
 * Deux régimes, et la différence est celle du statut :
 *
 * **`in_progress` : le statut l'emporte sur la date de fin prévue.** Elle n'est
 * qu'une prévision, et « en cours » dit précisément qu'elle a été dépassée sans
 * que le travail s'arrête. La respecter ferait cesser de surveiller exactement
 * les chantiers qui dérivent — ceux qu'on veut voir en premier. Défaut trouvé
 * en ouvrant l'écran le 7 septembre : le seul chantier en cours du tenant de
 * démonstration avait une fin prévue au 5 avril, et sortait donc du radar
 * depuis cinq mois alors qu'il tournait toujours.
 *
 * **`planned` : rien n'est attendu tant qu'aucun début n'est posé.** Sans date
 * de début, rien n'a été engagé, et le faire remonter mettrait au rouge tous
 * les chantiers qu'on n'a fait qu'ouvrir. Une fin sans début ne dit pas
 * davantage quand l'équipe devrait y être.
 */
function attenduCetteSemaine(
  project: ProjectSummary,
  weekStart: string,
  weekEnd: string,
): { from: string; to: string } | null {
  if (!ATTENDUS.has(project.status)) return null;

  if (project.status === "in_progress") {
    // Fin volontairement passée à `null` : sans terme, donc jusqu'au dimanche.
    // Le début, lui, borne encore — un chantier marqué en cours mais qui ne
    // commence que jeudi n'a personne à réclamer avant jeudi.
    return clampTo(project.plannedStartDate ?? weekStart, null, weekStart, weekEnd);
  }

  if (project.plannedStartDate == null) return null;
  // Un chantier prévu qui commence mardi sans fin annoncée est attendu de
  // mardi à dimanche : `clampTo` traite la fin nulle comme un sans-terme.
  return clampTo(project.plannedStartDate, project.plannedEndDate, weekStart, weekEnd);
}

/**
 * L'ordre dans lequel le patron doit voir les conflits.
 *
 * `assigned_while_absent` en tête, et ce n'est pas arbitraire : c'est le seul
 * des trois qui soit **invisible**. Un chantier sans personne se voit dans la
 * grille — la ligne est vide. Un salarié affecté alors qu'il est en congé
 * remplit sa case comme les autres : le planning a l'air complet, et l'équipe
 * découvre le trou le matin même, sur place.
 *
 * Le double chantier ferme la marche : il est aussi grave qu'un congé
 * oublié — personne n'est à deux endroits à la fois —, mais il se voit dans la
 * case, qui porte deux chantiers au même moment. Deux chantiers le même jour,
 * l'un le matin et l'autre l'après-midi, n'en sont pas un : c'est l'emploi du
 * temps ordinaire, et il ne déclenche plus rien.
 */
const GRAVITE: Record<PlanningConflictKind, number> = {
  assigned_while_absent: 0,
  unstaffed_project: 1,
  double_booking: 2,
};

export function sortPlanningConflicts(
  conflicts: readonly PlanningConflict[],
): PlanningConflict[] {
  return [...conflicts].sort((a, b) => {
    const parGravite = GRAVITE[a.kind] - GRAVITE[b.kind];
    if (parGravite !== 0) return parGravite;
    const parDate = a.from.localeCompare(b.from);
    if (parDate !== 0) return parDate;
    return (a.employeeName ?? "").localeCompare(b.employeeName ?? "");
  });
}

/**
 * Ce qu'un conflit dit, en une phrase.
 *
 * Écrit ici plutôt que dans l'écran pour la raison qui vaut partout dans ce
 * dépôt : l'agent conversationnel répondra à « qu'est-ce qui cloche la semaine
 * prochaine ? » et doit dire mot pour mot ce que la grille affiche. Deux
 * formulations pour un même fait finiraient par diverger, et c'est celle que
 * personne ne relit qui deviendrait fausse.
 */
const MOMENT: Record<DayPart, string> = {
  journee: "toute la journée",
  matin: "le matin",
  apres_midi: "l'après-midi",
};

export function conflictLabel(conflict: PlanningConflict): string {
  const qui = conflict.employeeName ?? "Ce salarié";
  const chantiers = conflict.projects.map((p) => p.name).join(" et ");

  switch (conflict.kind) {
    case "assigned_while_absent":
      return `${qui} est affecté à ${chantiers} alors qu'il est absent (${absenceKindLabel(
        conflict.absenceKind ?? "other",
      ).toLowerCase()}).`;
    case "unstaffed_project":
      return `${chantiers} : personne n'est affecté sur cette période.`;
    case "double_booking":
      return `${qui} est sur ${conflict.projects.length} chantiers en même temps, ${MOMENT[
        conflict.dayPart ?? "journee"
      ]} : ${chantiers}.`;
  }
}

/**
 * Les jours qui viennent, vus du terrain.
 *
 * Le compagnon ne demande pas une grille le dimanche soir : il demande « où
 * je vais demain ». Les jours sont donc regroupés par tranches — les journées
 * consécutives portant les mêmes chantiers n'en font qu'une — pour que la
 * réponse tienne en une ou deux lignes plutôt qu'en sept.
 *
 * **Le jour même est exclu, mais son chantier ne l'est pas.** C'est la faute
 * corrigée le 7 septembre, trouvée sur un vrai téléphone : la première version
 * écartait des jours suivants tout chantier déjà présent aujourd'hui. Le bloc
 * se vidait donc exactement dans le cas le plus courant du bâtiment — un
 * chantier qui dure des semaines — c'est-à-dire quand la réponse à « où je
 * vais demain » est « au même endroit ». C'est une réponse, et elle vaut
 * d'être affichée.
 *
 * Les journées sans rien ne figurent pas. Un « rien de prévu » sur un planning
 * que le conducteur n'a pas encore rempli se lirait comme une journée sans
 * travail, ce qui n'est pas ce qu'il veut dire.
 */
export interface UpcomingSpan {
  /** Premier jour de la tranche, AAAA-MM-JJ. */
  from: string;
  /** Dernier jour de la tranche. Égal à `from` pour une journée seule. */
  to: string;
  missions: Mission[];
}

const DAY_MS = 86_400_000;

function addDays(isoDate: string, n: number): string {
  return new Date(Date.parse(`${isoDate.slice(0, 10)}T00:00:00Z`) + n * DAY_MS)
    .toISOString()
    .slice(0, 10);
}

/** Les dates de `from` à `to` inclus. */
function weekDatesBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

const cleDe = (missions: readonly Mission[]): string =>
  missions.map((m) => `${m.projectId}:${m.dayPart}`).join("|");

/** Le matin avant l'après-midi, la journée entière en tête. */
const ORDRE_PART: Record<DayPart, number> = { journee: 0, matin: 1, apres_midi: 2 };

export function upcomingDays(
  missions: readonly Mission[],
  today: string,
  days = 7,
): UpcomingSpan[] {
  const spans: UpcomingSpan[] = [];

  for (let i = 1; i <= days; i += 1) {
    const date = addDays(today, i);
    const duJour = [...missions]
      .filter((m) => coversDay(m, date))
      .sort(
        (a, b) =>
          ORDRE_PART[a.dayPart] - ORDRE_PART[b.dayPart] ||
          a.projectName.localeCompare(b.projectName),
      );

    if (duJour.length === 0) continue;

    // Fusionner avec la tranche précédente seulement si elle s'arrête la
    // veille : deux lundis séparés par un mardi vide ne sont pas une période
    // continue, et les recoller ferait promettre un mardi qui n'existe pas.
    const precedente = spans[spans.length - 1];
    if (
      precedente &&
      precedente.to === addDays(date, -1) &&
      cleDe(precedente.missions) === cleDe(duJour)
    ) {
      precedente.to = date;
      continue;
    }

    spans.push({ from: date, to: date, missions: duJour });
  }

  return spans;
}

export interface BuildPlanningInput {
  /** N'importe quel jour de la semaine voulue : le lundi en est déduit. */
  weekStart: string;
  employees: readonly Employee[];
  assignments: readonly Assignment[];
  absences: readonly Absence[];
  projects: readonly ProjectSummary[];
}

/**
 * La semaine, assemblée.
 *
 * Les salariés sans la moindre affectation figurent avec sept cases vides :
 * c'est précisément eux qu'on cherche le vendredi soir. Une grille qui ne
 * montrerait que les gens occupés répondrait à la question qu'on ne se pose
 * pas.
 */
export function buildPlanningWeek(input: BuildPlanningInput): PlanningWeek {
  // Ramené au lundi ici et pas seulement dans le service : `weekDates` compte
  // sept jours à partir de ce qu'on lui donne, donc un mercredi produirait une
  // grille du mercredi au mardi. Elle aurait l'air d'une semaine, en
  // chevaucherait deux, et ne correspondrait plus aux bornes sur lesquelles
  // les affectations ont été lues.
  const dates = weekDates(startOfWeek(input.weekStart));
  const weekStart = dates[0] ?? input.weekStart;
  const weekEnd = dates[6] ?? input.weekStart;

  // Une seule fois, ici : tout ce qui suit — la grille comme les conflits —
  // travaille sur les absences accordées, et rien d'autre. Filtrer plus bas
  // aurait laissé un chemin oublier la règle.
  const effectif: BuildPlanningInput = {
    ...input,
    absences: input.absences.filter(blocksPlanning),
  };

  const rows: PlanningRow[] = effectif.employees.map((employee) => {
    const siennes = effectif.assignments.filter((a) => a.employeeId === employee.id);
    const absences = effectif.absences.filter((a) => a.employeeId === employee.id);

    const days: PlanningCell[] = dates.map((date) => {
      // `coversDay` et non `isActiveOn` : une affectation ne fait venir
      // personne le samedi ni le dimanche, sauf si ce jour a été coché.
      const couvrantes = siennes.filter((a) => coversDay(a, date));
      const projects = couvrantes
        .map((a) => ({
          assignmentId: a.id,
          projectId: a.projectId,
          projectName: a.projectName ?? "Chantier",
          roleOnSite: a.roleOnSite,
          dayPart: a.dayPart,
        }))
        .sort((a, b) => ORDRE_PART[a.dayPart] - ORDRE_PART[b.dayPart]);
      const occupees = new Set(couvrantes.flatMap((a) => halvesOf(a.dayPart)));

      const absence = absences.find((a) => isActiveOn(a, date)) ?? null;

      const weekend = weekendDayOf(date);
      return {
        date,
        projects,
        absence,
        busyHalves: HALF_DAYS.filter((h) => occupees.has(h)),
        free: weekend == null && projects.length === 0 && absence == null,
        weekend,
      };
    });

    const moities = (d: PlanningCell) => d.busyHalves.length / 2;
    return {
      employee,
      days,
      assignedDays: days.reduce((n, d) => n + moities(d), 0),
      absentDays: days.filter((d) => d.absence != null).length,
      // Un jour ouvré sans absence : ce qui n'est pas pris est libre, à la
      // demi-journée près.
      freeDays: days.reduce(
        (n, d) => (d.weekend == null && d.absence == null ? n + (1 - moities(d)) : n),
        0,
      ),
      weekendDays: days.reduce((n, d) => (d.weekend != null ? n + moities(d) : n), 0),
    };
  });

  return {
    weekStart,
    dates,
    rows,
    conflicts: sortPlanningConflicts(findConflicts({ ...effectif, weekStart, weekEnd }, rows)),
  };
}

/**
 * Ce qui cloche, cherché sur la semaine affichée et sur elle seule.
 *
 * Rogner à la semaine n'est pas une économie de calcul : un congé de trois
 * semaines signalé « du 1er au 21 » sur l'écran du 8 au 14 ferait douter de ce
 * qu'on regarde. Le conflit est borné à ce que la grille montre.
 */
function findConflicts(
  input: BuildPlanningInput & { weekStart: string; weekEnd: string },
  rows: readonly PlanningRow[],
): PlanningConflict[] {
  const conflicts: PlanningConflict[] = [];
  const { weekStart, weekEnd } = input;

  // ── Affecté pendant une absence ─────────────────────────────────────────
  // Une entrée par couple absence × chantier : deux chantiers pendant le même
  // congé sont deux trous à combler, pas un.
  for (const absence of input.absences) {
    const fenetre = clampTo(absence.startsOn, absence.endsOn, weekStart, weekEnd);
    if (!fenetre) continue;

    for (const assignment of input.assignments) {
      if (assignment.employeeId !== absence.employeeId) continue;
      // Jour par jour, et non période contre période : un congé du vendredi au
      // lundi ne gêne pas une affectation qui ne travaille pas le week-end
      // — mais il la gêne le vendredi et le lundi, et ce sont ces deux jours
      // qu'on veut voir.
      const jours = weekDatesBetween(fenetre.from, fenetre.to).filter((d) =>
        coversDay(assignment, d),
      );
      if (jours.length === 0) continue;

      conflicts.push({
        kind: "assigned_while_absent",
        from: jours[0]!,
        to: jours[jours.length - 1]!,
        employeeId: absence.employeeId,
        employeeName: absence.employeeName ?? assignment.employeeName,
        projects: [
          { id: assignment.projectId, name: assignment.projectName ?? "Chantier" },
        ],
        absenceKind: absence.kind,
        dayPart: null,
      });
    }
  }

  // ── Chantier sans personne ──────────────────────────────────────────────
  for (const project of input.projects) {
    const attendu = attenduCetteSemaine(project, weekStart, weekEnd);
    if (!attendu) continue;

    const couvert = input.assignments.some(
      (a) =>
        a.projectId === project.id &&
        periodsOverlap(a.startsOn, a.endsOn, attendu.from, attendu.to),
    );
    if (couvert) continue;

    conflicts.push({
      kind: "unstaffed_project",
      from: attendu.from,
      to: attendu.to,
      employeeId: null,
      employeeName: null,
      projects: [{ id: project.id, name: project.name }],
      absenceKind: null,
      dayPart: null,
    });
  }

  // ── Deux chantiers au même moment ───────────────────────────────────────
  // Cherché sur les cases déjà construites plutôt que sur les périodes : c'est
  // le jour qui pose problème, et une même paire de périodes peut se croiser
  // sur un jour comme sur cinq. Et demi-journée par demi-journée : le matin
  // sur un chantier et l'après-midi sur un autre n'est pas un conflit.
  for (const row of rows) {
    for (const cell of row.days) {
      const parMoitie = HALF_DAYS.map((half) => {
        // Un même chantier affecté deux fois — deux lignes qui se recouvrent —
        // n'est pas un double chantier, c'est une saisie en double. Elle ne
        // dérange personne sur le terrain : on compte les chantiers distincts.
        const distincts = new Map<string, { id: string; name: string }>();
        for (const p of cell.projects) {
          if (halvesOf(p.dayPart).includes(half)) {
            distincts.set(p.projectId, { id: p.projectId, name: p.projectName });
          }
        }
        return { half, projects: [...distincts.values()] };
      }).filter((m) => m.projects.length >= 2);
      if (parMoitie.length === 0) continue;

      // Les mêmes chantiers le matin et l'après-midi : un seul conflit, pour
      // toute la journée. Sinon, un par moitié.
      const cle = (ps: { id: string }[]) => ps.map((p) => p.id).sort().join("|");
      const groupes =
        parMoitie.length === 2 && cle(parMoitie[0]!.projects) === cle(parMoitie[1]!.projects)
          ? [{ dayPart: dayPartOf(["matin", "apres_midi"])!, projects: parMoitie[0]!.projects }]
          : parMoitie.map((m) => ({ dayPart: m.half as DayPart, projects: m.projects }));

      for (const g of groupes) {
        conflicts.push({
          kind: "double_booking",
          from: cell.date,
          to: cell.date,
          employeeId: row.employee.id,
          employeeName: employeeDisplayName(row.employee),
          projects: g.projects,
          absenceKind: null,
          dayPart: g.dayPart,
        });
      }
    }
  }

  return conflicts;
}
